import { Check, ChevronDown, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { localizedPath } from '@bannermatic/core';
import { api } from '../lib/api';
import { cn } from '../lib/format';
import { LOCALES, rememberLocale, useLocale, type Locale } from '../lib/i18n';
import { useSession } from '../lib/session';
import { usePublicPage } from '../marketing/PublicRoute';
import { Menu, MenuContent, MenuItem, MenuTrigger } from './ui';

/**
 * Language menu with the names of the languages. For signed-in users the choice is also saved
 * to the profile, so it follows them to other devices and is used for their emails. On public
 * pages it opens the page's version in that language (its own address, e.g. "/uk/terms").
 */
export function LanguageSwitcher({
  tone = 'light',
  compact,
  className,
}: {
  tone?: 'light' | 'dark';
  /** Below `sm` show the short code (EN, RU) instead of the language name. */
  compact?: boolean;
  className?: string;
}) {
  const { locale, setLocale } = useLocale();
  const user = useSession((s) => s.user);
  const page = usePublicPage();
  const navigate = useNavigate();
  const choose = (id: Locale) => {
    if (id === locale) return;
    if (page) {
      // the page in the other language sets it when it opens
      rememberLocale(id);
      navigate(localizedPath(id, page.locales.includes(id) ? page.path : '/'));
    } else setLocale(id);
    if (user) {
      useSession.getState().setSession({ ...user, locale: id }, useSession.getState().workspaces);
      void api('/api/auth/me', { method: 'PATCH', body: { locale: id }, workspace: false }).catch(
        () => undefined,
      );
    }
  };
  const dark = tone === 'dark';
  const current = LOCALES.find((l) => l.id === locale) ?? LOCALES[0];
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          data-testid="language-switcher"
          aria-label={current.name}
          className={cn(
            'inline-flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium transition outline-none',
            dark
              ? 'text-indigo-100/80 hover:bg-white/10 hover:text-white data-[state=open]:bg-white/10'
              : 'text-zinc-600 hover:bg-zinc-200/60 hover:text-zinc-900 data-[state=open]:bg-zinc-200/60',
            className,
          )}
        >
          <Globe className="size-4 shrink-0 opacity-70" />
          <span className={cn('flex-1 text-left', compact && 'hidden sm:inline')}>
            {current.name}
          </span>
          {compact && <span className="flex-1 text-left sm:hidden">{current.short}</span>}
          <ChevronDown
            className={cn('size-3.5 shrink-0 opacity-60', compact && 'hidden sm:block')}
          />
        </button>
      </MenuTrigger>
      <MenuContent align="end" className="min-w-40">
        {LOCALES.map((l) => (
          <MenuItem
            key={l.id}
            data-testid={`language-${l.id}`}
            onSelect={() => choose(l.id)}
            icon={<Check className={cn(l.id === locale ? 'text-indigo-600' : 'invisible')} />}
          >
            <span lang={l.id}>{l.name}</span>
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}
